import React from 'react';
import {
    Box,
    Drawer,
    IconButton,
    Link,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import PetsOutlinedIcon from '@mui/icons-material/PetsOutlined';
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined';
import ContentCutOutlinedIcon from '@mui/icons-material/ContentCutOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { brandColors } from '../Theme';
import { EXPLORE_ITEMS, EXPLORE_MENU_LABEL, NAV_ITEMS } from '../navigation';
import { CONTACT_EMAIL, LOCATION_LABEL } from '../site/contact';

const NAV_ICONS: Record<string, React.ElementType> = {
    '/': HomeOutlinedIcon,
    '/upcoming-litters': CalendarMonthOutlinedIcon,
    '/puppy-application': AssignmentOutlinedIcon,
    '/our-poodles': PetsOutlinedIcon,
};

const EXPLORE_ICONS: Record<string, React.ElementType> = {
    '/health-history': HistoryEduOutlinedIcon,
    '/grooming': ContentCutOutlinedIcon,
    '/about-us': GroupsOutlinedIcon,
};

const navItemSx = (isActive: boolean) => ({
    mb: 0.5,
    py: 1.4,
    px: 1.5,
    borderRadius: 2,
    transition: 'background-color 0.2s ease, color 0.2s ease',
    color: isActive ? 'primary.main' : 'text.primary',
    bgcolor: isActive ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
    borderLeft: '3px solid',
    borderColor: isActive ? 'primary.main' : 'transparent',
    '&:hover': {
        bgcolor: isActive ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)',
    },
    '&.Mui-selected': {
        bgcolor: 'rgba(25, 118, 210, 0.08)',
        '&:hover': {
            bgcolor: 'rgba(25, 118, 210, 0.12)',
        },
    },
});

type NavigationDrawerProps = {
    open: boolean;
    onClose: () => void;
};

const NavigationDrawer: React.FC<NavigationDrawerProps> = ({ open, onClose }) => {
    const location = useLocation();

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            ModalProps={{ keepMounted: true }}
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: 'rgba(44, 40, 37, 0.35)',
                        backdropFilter: 'blur(4px)',
                    },
                },
            }}
            PaperProps={{
                sx: {
                    width: 300,
                    maxWidth: '85vw',
                    bgcolor: '#ffffff',
                    boxShadow: '-8px 0 32px rgba(0, 0, 0, 0.14)',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <Box
                sx={{
                    bgcolor: '#fff',
                    borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
                    px: 2.5,
                    pt: 'max(20px, env(safe-area-inset-top))',
                    pb: 2.25,
                    position: 'relative',
                }}
            >
                <IconButton
                    aria-label="Close navigation menu"
                    onClick={onClose}
                    size="small"
                    sx={{
                        position: 'absolute',
                        top: 'max(12px, env(safe-area-inset-top))',
                        right: 10,
                        color: brandColors.lavenderDark,
                        bgcolor: 'rgba(128, 121, 154, 0.22)',
                        '&:hover': { bgcolor: 'rgba(128, 121, 154, 0.32)' },
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>

                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        pr: 5,
                        lineHeight: 1.35,
                        fontSize: '1.1rem',
                        color: '#1a1a1a',
                    }}
                >
                    Galia Standard Poodles
                </Typography>
                <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{ mt: 0.75, color: brandColors.lavenderDark }}
                >
                    <PlaceOutlinedIcon sx={{ fontSize: 16 }} />
                    <Typography variant="body2" sx={{ color: 'inherit' }}>
                        {LOCATION_LABEL}
                    </Typography>
                </Stack>
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <Typography
                component="p"
                variant="overline"
                sx={{
                    px: 2.5,
                    pt: 2,
                    pb: 0.5,
                    color: 'text.secondary',
                    letterSpacing: '0.1em',
                    fontSize: '0.68rem',
                }}
            >
                Pages
            </Typography>

            <List sx={{ px: 1.5, py: 0.5 }}>
                {NAV_ITEMS.map((item) => {
                    const isActive = location.pathname === item.path;
                    const Icon = NAV_ICONS[item.path] ?? HomeOutlinedIcon;

                    return (
                        <ListItemButton
                            key={item.path}
                            component={RouterLink}
                            to={item.path}
                            selected={isActive}
                            onClick={onClose}
                            sx={navItemSx(isActive)}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 40,
                                    color: isActive ? 'primary.main' : 'text.secondary',
                                }}
                            >
                                <Icon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary={item.label}
                                primaryTypographyProps={{
                                    fontWeight: isActive ? 600 : 500,
                                    fontSize: '0.9375rem',
                                }}
                            />
                            {isActive && (
                                <ChevronRightIcon
                                    fontSize="small"
                                    sx={{ color: 'primary.main', opacity: 0.85 }}
                                />
                            )}
                        </ListItemButton>
                    );
                })}
            </List>

            <Typography
                component="p"
                variant="overline"
                sx={{
                    px: 2.5,
                    pt: 1,
                    pb: 0.5,
                    color: 'text.secondary',
                    letterSpacing: '0.1em',
                    fontSize: '0.68rem',
                }}
            >
                {EXPLORE_MENU_LABEL}
            </Typography>

            <List sx={{ px: 1.5, py: 0.5, pb: 1.5 }}>
                {EXPLORE_ITEMS.map((item) => {
                    const isActive = location.pathname === item.path;
                    const Icon = EXPLORE_ICONS[item.path] ?? HistoryEduOutlinedIcon;

                    return (
                        <ListItemButton
                            key={item.path}
                            component={RouterLink}
                            to={item.path}
                            selected={isActive}
                            onClick={onClose}
                            sx={navItemSx(isActive)}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 40,
                                    color: isActive ? 'primary.main' : 'text.secondary',
                                }}
                            >
                                <Icon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                                primary={item.label}
                                primaryTypographyProps={{
                                    fontWeight: isActive ? 600 : 500,
                                    fontSize: '0.9375rem',
                                }}
                            />
                            {isActive && (
                                <ChevronRightIcon
                                    fontSize="small"
                                    sx={{ color: 'primary.main', opacity: 0.85 }}
                                />
                            )}
                        </ListItemButton>
                    );
                })}
            </List>
            </Box>

            <Box
                sx={{
                    px: 2,
                    py: 2,
                    borderTop: 1,
                    borderColor: 'divider',
                    bgcolor: '#fafafa',
                }}
            >
                <Link
                    href={`mailto:${CONTACT_EMAIL}`}
                    onClick={onClose}
                    underline="none"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        py: 1.25,
                        px: 2,
                        borderRadius: 2,
                        color: 'primary.main',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        border: '1px solid',
                        borderColor: 'primary.main',
                        bgcolor: '#fff',
                        transition: 'background-color 0.2s ease',
                        '&:hover': {
                            bgcolor: 'rgba(25, 118, 210, 0.06)',
                        },
                    }}
                >
                    <EmailOutlinedIcon sx={{ fontSize: 18 }} />
                    Email us
                </Link>
            </Box>
        </Drawer>
    );
};

export default NavigationDrawer;
